import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import './HMSTRAIChat.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../utils/axiosConfig';
import { OfferContext } from '../OfferContext';
import { AuthContext } from '../context/AuthContext';
import withAuth from '../utils/withAuth';
import { v4 as uuidv4 } from 'uuid';
import { getErrorMessage } from '../utils/errorMessages';

// Copy of the helper function from ChatWindow
const formatMessageLine = (line) => {
  return line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
};

function HMSTRAIChat({ key, isNewChat }) {
  // Copy all the state and refs from ChatWindow
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [hasMicPermission, setHasMicPermission] = useState(null);
  const [hasMicCapability, setHasMicCapability] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [audioData, setAudioData] = useState([]);
  const [threadId] = useState(uuidv4());

  // New state for audio functionality
  const [voiceModeEnabled, setVoiceModeEnabled] = useState(localStorage.getItem('voiceModeEnabled') === 'true');
  const [isAudioPrimed, setIsAudioPrimed] = useState(false);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  const [audioReady, setAudioReady] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [showVoiceModal, setShowVoiceModal] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const animationFrameRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  // One AudioContext for the whole component, built on first real need and
  // reused. Browsers hard-cap these (~6 per document). The warm-up and priming
  // paths used to build their own and park them in React state, which the
  // unmount cleanup never closed (it only closes audioContextRef) - and this
  // chat is fully remounted every time the user starts a new chat, so the
  // leak compounded. Returns null rather than throwing, so a device held
  // exclusively by something else degrades to text-only.
  const getAudioContext = useCallback(() => {
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      return audioContextRef.current;
    }
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return null;
      audioContextRef.current = new Ctor();
      return audioContextRef.current;
    } catch (error) {
      console.warn('AudioContext unavailable, voice features disabled:', error);
      audioContextRef.current = null;
      return null;
    }
  }, []);

  const { user } = useContext(AuthContext);
  const [fileName, setFileName] = useState('');
  const { setOfferResponse } = useContext(OfferContext);

  const dealOptions = [
    { text: "Deals, Recaps, Deliveries relationship", prompt: "Give me a user friendly rundown of how deals, recaps and deliveries are linked with an example" },
    { text: "Most shipped products", prompt: "What are the most shipped products and their quantities in my recaps?" },
    { text: "Shipping ports", prompt: "Give me a deep analysis of my top three shipping ports and the coal being shipped" },
    { text: "Vessel Rundown", prompt: "Give me a rundown of my vessels and their associated data" },
  ];

  // Clear messages when isNewChat changes
  useEffect(() => {
    if (isNewChat) {
      setMessages([]);
      setInput('');
      initializeNewChat();
    }
  }, [key]);

  const initializeNewChat = async () => {
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    if (!isLoggedIn || !user?.id) return;

    // Warm the chat LLM endpoint so the first reply doesn't wait on a cold GPU
    // boot. Best-effort and debounced server-side; failure is silent.
    axiosInstance.post('/api/knowledge-hub/warm-llm/').catch(() => {});

    try {
      const formData = new FormData();
      formData.append('input', '');
      formData.append('new_chat', 'true');
      formData.append('thread_id', threadId);

      await axiosInstance.post(
        '/api/offer/hms-interrogation/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
    } catch (error) {
      console.error('Error initializing new chat:', error);
    }
  };

  // Adjust the height of the textarea
  const autoResizeTextarea = () => {
    const textarea = inputRef.current;
    if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    }
    };

    useEffect(() => {
    autoResizeTextarea();
    }, [input]);

  // Scroll to the bottom of chat messages (not entire page)
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [messages]);
  

  const handleOptionClick = (prompt) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  const handleSend = async () => {
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    if (!isLoggedIn || !user?.id) {
      console.error('User is not authenticated');
      setMessages(prevMessages => [
        ...prevMessages,
        { text: 'Please log in to continue.', sender: 'assistant' }
      ]);
      return;
    }

    if (input.trim() === '') return;

    setMessages((prevMessages) => [
      ...prevMessages,
      { 
        text: input, 
        sender: 'user',
        timestamp: new Date().toISOString()
      },
    ]);
    setInput('');
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('input', input);
      formData.append('thread_id', threadId);
      formData.append('voice_preference', getSelectedVoice());

      const response = await axiosInstance.post(
        '/api/offer/hms-interrogation/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setMessages((prevMessages) => [
        ...prevMessages,
        { 
          text: response.data.message, 
          sender: 'assistant',
          timestamp: new Date().toISOString()
        },
      ]);

      // Play audio response if available
      if (response.data.audio_response) {
        playAudioResponse(response.data.audio_response);
      }

    } catch (error) {
      console.error('Error processing input:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          text: getErrorMessage(error, 'Message could not be processed. Please try again.'),
          sender: 'assistant',
          timestamp: new Date().toISOString()
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent default behavior (new line)
      handleSend();
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setFileName(file.name);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const clearFile = (event) => {
    event.stopPropagation(); // Prevents the file explorer from opening
    setFileName('');
  };

  // Check if device has microphone capability
  useEffect(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const hasMic = devices.some(device => device.kind === 'audioinput');
          setHasMicCapability(hasMic);
          console.log('Microphone capability:', hasMic);
        })
        .catch(err => {
          console.error('Error checking microphone capability:', err);
          setHasMicCapability(false);
        });
    } else {
      setHasMicCapability(false);
      console.log('MediaDevices API not supported');
    }
  }, []);

  // Clean up audio resources when component unmounts
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      stopCurrentAudio();
    };
  }, []);

  // Add audio warm-up effect
  useEffect(() => {
    const warmAudioContext = async () => {
      try {
        const context = getAudioContext();
        if (!context) {
          setIsAudioPrimed(false);
          return;
        }

        if (context.state === 'suspended') {
          await context.resume();
        }

        const silentAudio = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQgAAAAA');
        silentAudio.muted = true;
        silentAudio.autoplay = true;
        
        try {
          await silentAudio.play();
          silentAudio.pause();
          setIsAudioPrimed(true);
        } catch (error) {
          console.warn('Failed to warm up audio context:', error);
          setIsAudioPrimed(false);
        }
      } catch (error) {
        console.error('Error during audio context warm-up:', error);
        setIsAudioPrimed(false);
      }
    };

    if (!isAudioPrimed && voiceModeEnabled) {
      const handleFirstClick = async () => {
        await warmAudioContext();
        document.removeEventListener('click', handleFirstClick);
      };
      document.addEventListener('click', handleFirstClick, { once: true });
      
      return () => {
        document.removeEventListener('click', handleFirstClick);
      };
    }
  }, [getAudioContext, voiceModeEnabled, isAudioPrimed]);

  const stopCurrentAudio = () => {
    if (audioPlayerRef.current) {
      console.log('Stopping current audio playback...');
      audioPlayerRef.current.pause();
      audioPlayerRef.current.src = '';
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
      }
      audioPlayerRef.current = null;
      setCurrentAudioUrl(null);
      setAudioReady(false);
    }
  };

  const handleVoiceSelection = (voice) => {
    setSelectedVoice(voice);
    localStorage.setItem('selectedVoice', voice);
    setShowVoiceModal(false);
    startRecording();
  };

  const getSelectedVoice = () => {
    return selectedVoice || localStorage.getItem('selectedVoice') || 'am_echo';
  };

  const playAudioResponse = (base64Audio) => {
    try {
      if (!base64Audio) {
        console.error('No audio data received');
        return;
      }
      console.log('Processing audio response, length:', base64Audio.length);

      const binaryString = window.atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const audioBlob = new Blob([bytes], { type: 'audio/wav' });

      stopCurrentAudio();

      const audio = new Audio();
      audio.autoplay = true;
      audioPlayerRef.current = audio;
      const audioUrl = URL.createObjectURL(audioBlob);
      setCurrentAudioUrl(audioUrl);

      audio.addEventListener('loadstart', () => console.log('Audio loading started'));
      audio.addEventListener('loadeddata', async () => {
        if (voiceModeEnabled && isAudioPrimed) {
          try {
            await audio.play();
            console.log('Autoplay started successfully');
          } catch (error) {
            console.warn('Autoplay failed, falling back to manual play:', error);
            setAudioReady(true);
          }
        } else {
          console.log('Voice mode disabled or audio not primed, enabling manual play button');
          setAudioReady(true);
        }
      });

      audio.addEventListener('playing', () => console.log('Audio playback started'));
      audio.addEventListener('pause', () => console.log('Audio playback paused'));
      audio.addEventListener('ended', () => {
        console.log('Audio playback completed');
        setAudioReady(false);
      });

      audio.addEventListener('error', (e) => {
        console.error('Audio playback error:', e);
        setAudioReady(true);
      });

      audio.preload = 'auto';
      audio.src = audioUrl;

    } catch (error) {
      console.error('Error in playAudioResponse:', error);
      setAudioReady(true);
    }
  };

  const handlePlayAudio = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.play()
        .catch(error => {
          console.error('Playback failed:', error);
        });
    }
  };

  const toggleRecording = async () => {
    if (isRecording) {
      stopRecording();
    } else {
      // Always stop any playing audio before starting new recording
      stopCurrentAudio();

      if (!selectedVoice) {
        setShowVoiceModal(true);
      } else {
        try {
          // Only try to initialize audio if not already primed
          if (voiceModeEnabled && !isAudioPrimed) {
            console.log('Audio not primed, attempting to prime...');
            const context = getAudioContext();
            if (context) {
              await context.resume();
              setIsAudioPrimed(true);
            }
          }
          
          // Proceed with recording
          await startRecording();
          
        } catch (error) {
          console.error('Error in toggleRecording:', error);
          // Still try to start recording even if audio initialization fails
          await startRecording();
        }
      }
    }
  };

  const startRecording = async () => {
    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);
      setHasMicPermission(true);
      
      // Set up or resume the shared audio context
      const context = getAudioContext();
      if (!context) {
        throw new Error('Audio is unavailable on this device');
      }
      if (context.state === 'suspended') {
        await context.resume();
      }

      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      const source = context.createMediaStreamSource(stream);
      source.connect(analyser);
      
      // Set up media recorder
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        audioChunksRef.current = [];
        processAudioData(audioBlob);
      };
      
      // Add initial recording message
      setMessages(prev => [...prev, { 
        text: 'Processing audio...',
        sender: 'user',
        timestamp: new Date().toISOString()
      }]);
      
      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
      
      // Start visualizing audio
      visualizeAudio();
      
    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setHasMicPermission(false);
        alert('Microphone permission denied. Please allow microphone access to use this feature.');
      } else {
        alert('Error starting recording. Please try again.');
      }
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
        setAudioStream(null);
      }
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      setIsRecording(false);
      setAudioData([]);
    }
  };
  
  const visualizeAudio = () => {
    if (!analyserRef.current) return;
    
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const updateVisualization = () => {
      if (!analyserRef.current) return;
      
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // We'll use a subset of the frequency data for visualization
      const visualData = Array.from(dataArray.slice(0, 20))
        .map(value => value / 255); // Normalize to 0-1
        
      setAudioData(visualData);
      
      animationFrameRef.current = requestAnimationFrame(updateVisualization);
    };
    
    updateVisualization();
  };
  
  const processAudioData = async (audioBlob) => {
    try {
      setIsLoading(true);
      
      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      
      // Create form data to send to backend
      const formData = new FormData();
      formData.append('voice_file', audioBlob, 'recording.wav');
      formData.append('thread_id', threadId);
      formData.append('voice_preference', getSelectedVoice());
      
      // Send the audio file to the backend with signal
      const response = await axiosInstance.post(
        '/api/offer/hms-interrogation/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          signal: abortControllerRef.current.signal
        }
      );
      
      // Only process response if component is still mounted
      if (!isMountedRef.current) return;
      
      // Update the last message (which should be the "Processing audio..." message)
      setMessages(prev => {
        const newMessages = [...prev];
        if (newMessages.length > 0) {
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage.text === 'Processing audio...') {
            lastMessage.text = response.data.transcribed_text || 'Could not transcribe audio';
          }
        }
        return newMessages;
      });
      
      // Add the assistant's response if present
      if (response.data.message) {
        setMessages(prev => [...prev, {
          text: response.data.message,
          sender: 'assistant',
          timestamp: new Date().toISOString()
        }]);

        // Only play audio if component is still mounted
        if (isMountedRef.current && response.data.audio_response) {
          playAudioResponse(response.data.audio_response);
        }
      }
      
    } catch (error) {
      if (!isMountedRef.current) return;
      
      // Only show error if it's not an abort error
      if (error.name !== 'AbortError') {
        console.error('Error processing audio:', error);
        setMessages(prev => [...prev, { 
          text: getErrorMessage(error, 'Message could not be processed. Please try again.'),
          sender: 'assistant',
          timestamp: new Date().toISOString()
        }]);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="hmstr-chat-window">
      <div className="hmstr-chat-messages">
        {messages.length === 0 && (
          <div className="hmstr-chat-placeholder">
            <div className="hmstr-placeholder-content">
              <p>Select an option to analyze your data</p>
              <div className="hmstr-options-grid">
                {dealOptions.map((option, index) => (
                  <div
                    key={index}
                    className="hmstr-option-rectangle"
                    onClick={() => handleOptionClick(option.prompt)}
                  >
                    <span>{option.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`hmstr-chat-bubble ${msg.sender === 'user' ? 'user' : 'assistant'}`}
          >
            {msg.text.split('\n').map((line, i) => (
              <p 
                key={i} 
                dangerouslySetInnerHTML={{ __html: formatMessageLine(line) }}
              />
            ))}
            {msg.sender === 'assistant' && !voiceModeEnabled && audioReady && (
              <button 
                onClick={handlePlayAudio}
                className="hmstr-play-audio-button"
              >
                Play Audio
              </button>
            )}
            <div className="hmstr-message-timestamp">
              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="hmstr-chat-bubble assistant">
            <div className="hmstr-typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="hmstr-chat-input-area">
        <textarea
          ref={inputRef}
          className="hmstr-chat-input"
          placeholder="Interrogate your data with HAMSTIR..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleInputKeyPress}
          disabled={isLoading}
        />
        <button className="hmstr-send-button" onClick={handleSend} disabled={isLoading}>
          <FontAwesomeIcon icon={faPaperPlane} />
        </button>
      </div>
    </div>
  );
}

export default withAuth(HMSTRAIChat); 